import React, { FC, useEffect, useState } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';

import { COLORS } from 'styles/constants';
import { UserObjectType, UserShortSchema } from 'constants/graphqlTypes';
import { REF_PLACEHOLDER } from 'components/Explore/Item/helpers';
import { getUserInfo } from 'components/Profile/ProfileTop/helpers';
import { transformRoute } from 'helpers/routingHelper';
import { ROUTE_USER_PROFILE } from 'routes';
import GraphemeSplitter from 'grapheme-splitter';

import StyledAvatar from './styled';
import AvatarPlaceholder from './AvatarPlaceholder';

export interface IAvatarProps {
  user: UserObjectType | UserShortSchema;
  dimension?: number;
  color?: string;
  dataTestId?: string;
  isAnimated?: boolean;
  clickable?: boolean;
}

const Avatar: FC<IAvatarProps> = ({
  dimension = 44,
  color = COLORS.white[100],
  user,
  dataTestId,
  isAnimated = false,
  clickable = false,
}) => {
  const { username, firstName, lastName, avatar } = getUserInfo(user);
  const splitter = new GraphemeSplitter();
  const location = useLocation();
  const params = useParams<{ feedId?: string; pickId?: string }>();
  const { push } = useHistory();

  // Check if we're on the FeedItem route
  const isFeedItemRoute = Boolean(
    params.feedId && params.pickId && location.pathname.includes(`/feed/${params.feedId}/${params.pickId}`)
  );

  const [animationClass, setAnimationClass] = useState(isAnimated && !isFeedItemRoute ? 'animated' : '');

  useEffect(() => {
    if (!isAnimated || isFeedItemRoute) {
      return undefined;
    }

    // Set initial position
    setAnimationClass('animated');

    // Start animation after a brief delay
    const timer = setTimeout(() => {
      setAnimationClass('animated-end');
    }, 100);

    return () => clearTimeout(timer);
  }, [isAnimated, isFeedItemRoute]);

  const getFirstSymbol = (name?: string | null) => {
    if (!name) return '';
    return splitter.splitGraphemes(name)[0];
  };

  const handleAvatarClick = () => {
    if (clickable && username) {
      push(transformRoute(ROUTE_USER_PROFILE, { profileName: username }));
    }
  };

  const initialsLetters = `${getFirstSymbol(firstName)}${getFirstSymbol(lastName)}`;
  const isDeletedUser = user?.id === REF_PLACEHOLDER;

  return isDeletedUser ? (
    <AvatarPlaceholder dataTestId={dataTestId} dimension={dimension} />
  ) : (
    <StyledAvatar
      data-testid={dataTestId || ''}
      color={color}
      dimension={dimension}
      src={avatar}
      className={`avatar ${animationClass}`}
      isGreenBorder={false}
      $isFeedItemRoute={isFeedItemRoute}
      clickable={clickable}
      onClick={handleAvatarClick}
    >
      {!avatar && (initialsLetters || username[0])}
    </StyledAvatar>
  );
};

export default Avatar;
